import {
  BacenStatus,
  ContactBacenReport,
  ContactRiskClassification,
  Occurrence,
  OpportunityContactRole,
  Report,
  RiskClassificationLevel,
  SerasaApiStatus
} from '@indigo-ag/schema';
import { brazil } from 'shared';
import { getContactNameRole } from '../utils/backgroundCheck.util';

export type SerasaBacenStatus = 'CLEARED' | 'FAILED' | 'UNRESOLVED';

interface BaseReport {
  taxIdentifier: string;
  status: SerasaApiStatus | BacenStatus;
  dateReport?: Date;
}

export interface BacenItem extends BaseReport {
  overdueValue: number;
  financialLossValue: number;
  fullResponsibilityValue: number;
  percentageOfDebts: number;
  reasons: string[];
}

export interface SerasaItem extends BaseReport {
  occurrences: Occurrence[];
}

export interface SerasaBacenItem {
  contactName: string;
  taxIdentifier: string;
  overallStatus: SerasaBacenStatus;
  serasa?: SerasaItem;
  bacen?: BacenItem;
}

export interface ContactRiskClassificationItem extends ContactRiskClassification {
  contactName: string;
  status: 'SUCCESS' | 'FAILURE' | 'PENDING';
}

const getContactName = (taxIdentifier: string, contactRoles: OpportunityContactRole[]) => {
  taxIdentifier = brazil.cleanCpfCnpjValue(taxIdentifier);
  const role = contactRoles.find(role => role.contact.taxIdentifier === taxIdentifier);
  return role ? getContactNameRole(role) : '';
};

const buildContactSerasaReport = (taxIdentifier: string, serasaReports: Report[]) => {
  const serasa = serasaReports.find(
    serasa => brazil.cleanCpfCnpjValue(serasa.taxIdentifier) === brazil.cleanCpfCnpjValue(taxIdentifier)
  );

  return serasa
    ? {
        ...serasa,
        occurrences: serasa?.occurrences ?? []
      }
    : undefined;
};

const buildContactBacenReport = (taxIdentifier: string, bacenReports: ContactBacenReport[]): BacenItem | undefined => {
  const bacen = bacenReports.find(
    bacen => brazil.cleanCpfCnpjValue(bacen.taxIdentifier) === brazil.cleanCpfCnpjValue(taxIdentifier)
  );
  return bacen
    ? {
        ...bacen,
        overdueValue: bacen.overdueValue ?? 0,
        financialLossValue: bacen.financialLossValue ?? 0,
        fullResponsibilityValue: bacen.fullResponsibilityValue ?? 0,
        percentageOfDebts: bacen.percentageOfDebts ?? 0,
        reasons: bacen.reasons ?? []
      }
    : undefined;
};

export const getOverallSerasaBacenStatus = (
  serasa?: SerasaItem | Report,
  bacen?: BacenItem | ContactBacenReport
): SerasaBacenStatus => {
  const STATUS_RULES: { condition: boolean; status: SerasaBacenStatus }[] = [
    {
      condition: !serasa && !bacen,
      status: 'UNRESOLVED'
    },
    {
      condition: serasa?.status === 'UNRESOLVED' || bacen?.status === 'UNRESOLVED',
      status: 'UNRESOLVED'
    },
    {
      condition: serasa?.status === 'FAILED' || bacen?.status === 'FAILED',
      status: 'FAILED'
    },
    {
      condition: serasa?.status === 'CLEARED' && bacen?.status === 'CLEARED',
      status: 'CLEARED'
    },
    {
      condition: serasa?.status === 'CLEARED' && bacen?.status === undefined,
      status: 'CLEARED'
    },
    {
      condition: !serasa?.status && bacen?.status === 'CLEARED',
      status: 'CLEARED'
    }
  ];

  const matchingRule = STATUS_RULES.find(rule => rule.condition);
  return matchingRule?.status ?? 'UNRESOLVED';
};

export const getSerasaBacenList = (
  contactRoles: OpportunityContactRole[],
  serasaReports: Report[],
  bacenReports: ContactBacenReport[]
) => {
  const serasaBacenList: SerasaBacenItem[] = [];

  contactRoles.forEach(role => {
    const taxIdentifier = role.contact.taxIdentifier ?? '';
    const contactName = getContactName(taxIdentifier, contactRoles);
    const serasa = buildContactSerasaReport(taxIdentifier, serasaReports);
    const bacen = buildContactBacenReport(taxIdentifier, bacenReports);

    serasaBacenList.push({
      contactName,
      taxIdentifier,
      serasa,
      bacen,
      overallStatus: getOverallSerasaBacenStatus(serasa, bacen)
    });
  });
  return serasaBacenList;
};

const getRiskClassificationStatus = ({ riskClassificationLevel, isExpired }: ContactRiskClassification) => {
  if (!riskClassificationLevel || riskClassificationLevel === RiskClassificationLevel.NOT_CLASSIFIED) {
    return 'PENDING';
  }

  if (
    (riskClassificationLevel &&
      [RiskClassificationLevel.VERY_HIGH, RiskClassificationLevel.HIGH].includes(riskClassificationLevel)) ||
    isExpired
  ) {
    return 'FAILURE';
  }

  return 'SUCCESS';
};

export const getRiskClassificationList = (
  contactRoles: OpportunityContactRole[],
  riskClassifications: ContactRiskClassification[]
) => {
  return riskClassifications.map<ContactRiskClassificationItem>(riskClassification => {
    const contact = contactRoles.find(
      role =>
        brazil.cleanCpfCnpjValue(role.contact.taxIdentifier ?? '') ===
        brazil.cleanCpfCnpjValue(riskClassification.contactTaxIdentifier)
    );
    return {
      ...riskClassification,
      contactName: contact ? getContactNameRole(contact) : '',
      status: getRiskClassificationStatus(riskClassification)
    };
  });
};
